import { useForm } from 'react-hook-form'
import clsx from 'clsx'
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'

import { Button } from '@wartek-id/button-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'

import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { ForwardedRef } from 'react'
import type { Kinerja, SKPTabHandler, UpdateSKPPayload } from 'types/kinerja'

const errorStateClass = 'perilaku-kerja-has-error'
const errorStateMessage = 'Wajib diisi'

interface TabPerilakuKerjaProps {
  skp: Kinerja
  isLoading?: boolean
  onClickNext?: (data: UpdateSKPPayload) => void
  onClickSave?: (data: UpdateSKPPayload) => void
}

const TabPerilakuKerja = (
  {
    skp,
    isLoading = false,
    onClickNext = () => {},
    onClickSave = () => {},
  }: TabPerilakuKerjaProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { trackEvent } = useSKPTrackEvent()
  const { contentPerilakuKerja } = useContentContext()

  const selectedPrilakuKerja = Array.from({
    length: contentPerilakuKerja?.aspek.length,
  }).map((_, index) => ({
    aspekId: skp?.prilakuKerja.at(index)?.aspekId,
    indikatorId: skp?.prilakuKerja.at(index)?.indikatorId,
    ekspektasiId: skp?.prilakuKerja.at(index)?.ekspektasiId,
  }))

  const defaultValues = useMemo(
    () => ({
      indikatorPerilaku:
        selectedPrilakuKerja.map((prilakuKerja, index) => {
          const indikator = contentPerilakuKerja?.aspek[index]?.indikator

          const selectedIndikator = indikator?.find(
            (indikator) => indikator.id === prilakuKerja.indikatorId
          )

          const result: OptionItem = {
            value: prilakuKerja.indikatorId,
            name: selectedIndikator?.value,
          }

          return result
        }) ?? [],
      perwujudanPerilaku:
        selectedPrilakuKerja.map((prilakuKerja, index) => {
          const indikator = contentPerilakuKerja?.aspek[index]?.indikator

          const ekspektasi = indikator?.find(
            (indikator) => indikator.id === prilakuKerja.indikatorId
          )?.ekspektasi

          const selectedEkspektasi = ekspektasi?.find(
            (ekspektasi) => ekspektasi.id === prilakuKerja.ekspektasiId
          )

          const result: OptionItem = {
            value: prilakuKerja.ekspektasiId,
            name: selectedEkspektasi?.value,
          }

          return result
        }) ?? [],
    }),
    [selectedPrilakuKerja, contentPerilakuKerja]
  )

  const { setValue, getValues, watch, clearErrors, setError, formState } =
    useForm<{
      indikatorPerilaku: OptionItem[]
      perwujudanPerilaku: OptionItem[]
    }>({
      defaultValues,
    })

  const { perwujudanPerilaku } = watch()

  const validateValues = () => {
    clearErrors()

    perwujudanPerilaku.forEach((value, i) => {
      if (!value?.name)
        setError(`perwujudanPerilaku.${i}`, {
          message: errorStateMessage,
        })
    })

    const elementsWithError = document.getElementsByClassName(errorStateClass)
    if (elementsWithError.length) {
      // Scroll to first element that has errors
      const element = elementsWithError[0]

      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const isDisabledNextButton =
    !perwujudanPerilaku?.length ||
    perwujudanPerilaku?.some(
      (perwujudan) => !perwujudan?.value || isNaN(perwujudan.value)
    )

  const generatePayload = useCallback((): UpdateSKPPayload => {
    const prilakuKerja = contentPerilakuKerja?.aspek.map((a, i) => ({
      aspekId: a.id,
      indikatorId: getValues('indikatorPerilaku')[i]?.value,
      ekspektasiId: getValues('perwujudanPerilaku')[i]?.value,
    }))

    return {
      id: skp.id,
      prilakuKerja,
    }
  }, [contentPerilakuKerja, getValues, skp])

  const onSubmit = () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_TO_RANGKUMAN',
      screen_name: 'KINERJA_PLANNING_PERILAKU_KERJA_PAGE',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })
    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    trackEvent({
      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
      screen_name: 'KINERJA_PLANNING_PERILAKU_KERJA_PAGE',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })
    onClickSave(generatePayload())
  }, [trackEvent, skp, onClickSave, generatePayload])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <>
      <div className="p-9">
        <Text variant="display-sm">Indikator dan Perwujudan Perilaku</Text>
        <Text className="mt-1">
          Untuk masing-masing aspek, pilih satu yang Anda ingin prioritaskan di
          semester ini.
          <Link
            href={`${PENGELOLAAN_KINERJA_GURU_PATH}/panduan/perilaku-kerja`}
            target="_blank"
            rel="noopener"
            className="!pr-0"
          >
            Selengkapnya.
          </Link>
        </Text>

        <div className="mt-10 flex flex-col gap-6">
          {contentPerilakuKerja?.aspek.map(
            (perilakuKerja, perilakuKerjaIndex) => (
              <div
                key={`perilaku-kerja-${perilakuKerjaIndex}`}
                data-testid={`perilaku-kerja-${perilakuKerjaIndex}`}
                className={clsx('p-6 border border-gray-30 rounded', {
                  '!border-[#D93640]':
                    !!formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex],
                  [errorStateClass]:
                    !!formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex],
                })}
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-2.5 items-start">
                    <div className="flex items-center justify-center rounded-full bg-blue-70 p-3">
                      <div className="absolute flex">
                        <Text color="inverse" className="!font-bold">
                          {perilakuKerjaIndex + 1}
                        </Text>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Text variant="body-sm" color="subdued">
                        Aspek
                      </Text>
                      <Text variant="display-sm">{perilakuKerja.value}</Text>
                    </div>
                  </div>

                  <Text color="critical">
                    {
                      formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex]
                        ?.message
                    }
                  </Text>
                </div>

                <Separator className="mt-6" />

                <div className="mt-6">
                  <Text className="mb-3">
                    Apa perilaku yang paling ingin Anda fokuskan di tahun ini?
                  </Text>
                  <Text variant="body-sm" color="subdued" className="mb-1">
                    Indikator Perilaku
                  </Text>
                  <CustomSelect
                    value={getValues(`indikatorPerilaku.${perilakuKerjaIndex}`)}
                    placeholder="Pilih"
                    isInvalid={
                      !!formState.errors.perwujudanPerilaku?.[
                        perilakuKerjaIndex
                      ]?.message
                    }
                    options={perilakuKerja.indikator.map((indikator) => ({
                      value: indikator.id,
                      name: indikator.value,
                    }))}
                    onChange={(selectedValue: OptionItem) => {
                      setValue(
                        `indikatorPerilaku.${perilakuKerjaIndex}`,
                        selectedValue
                      )

                      // Reset Perwujudan Perilaku currently selected
                      setValue(
                        `perwujudanPerilaku.${perilakuKerjaIndex}`,
                        undefined
                      )
                    }}
                    data-testid="select-indikator"
                  />
                </div>

                <div className="mt-4">
                  <Icon>arrow_downward</Icon>
                </div>

                <div className="mt-4">
                  <Text className="mb-3">
                    Bagaimana Anda akan menerapkan perilaku tersebut dalam
                    kehidupan sehari-hari?
                  </Text>
                  <Text variant="body-sm" color="subdued" className="mb-1">
                    Perwujudan Perilaku
                  </Text>
                  <CustomSelect
                    value={getValues(
                      `perwujudanPerilaku.${perilakuKerjaIndex}`
                    )}
                    placeholder="Pilih"
                    isInvalid={
                      !!formState.errors.perwujudanPerilaku?.[
                        perilakuKerjaIndex
                      ]?.message
                    }
                    options={
                      perilakuKerja.indikator
                        .find(
                          (indikator) =>
                            indikator.id ===
                            getValues(`indikatorPerilaku.${perilakuKerjaIndex}`)
                              ?.value
                        )
                        ?.ekspektasi?.map((ekspektasi) => ({
                          value: ekspektasi.id,
                          name: ekspektasi.value,
                        })) ?? []
                    }
                    onChange={(selectedValue: OptionItem) => {
                      setValue(
                        `perwujudanPerilaku.${perilakuKerjaIndex}`,
                        selectedValue
                      )
                    }}
                    isDisabled={
                      !watch(`indikatorPerilaku.${perilakuKerjaIndex}.value`)
                    }
                    data-testid="select-perwujudan-perilaku"
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <StickyFooter>
        <Button
          size="lg"
          color="white"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onSaveDraft}
        >
          Simpan draf
        </Button>
        <Button
          size="lg"
          color="black"
          isLoading={isLoading}
          disabled={isDisabledNextButton || isLoading}
          onClick={onSubmit}
          onClickCapture={validateValues}
        >
          Ke Rangkuman
        </Button>
      </StickyFooter>
    </>
  )
}

export default forwardRef<SKPTabHandler, TabPerilakuKerjaProps>(
  TabPerilakuKerja
)
